import { useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { ArrowDownTrayIcon, DocumentArrowUpIcon } from '@heroicons/react/24/outline';
import { Badge, Button, Card, CardHeader } from '@/components/common';
import { getErrorMessage } from '@/services';
import {
  hospitalNetworkApi,
  type ImportBatchDetail,
  type ImportOutcome,
  type ImportRow,
  validateHospitalListFile,
} from '@/services/hospitalNetwork';

/**
 * DQ-346: upload an insurer's hospital list, look at the dry-run preview, then
 * commit or discard it. The preview writes nothing; commit applies the New and
 * Matched rows, and Needs-review rows wait for the country admin.
 */

const OUTCOME_TABS: { key: ImportOutcome; label: string }[] = [
  { key: 'new', label: 'New' },
  { key: 'matched_auto', label: 'Matched' },
  { key: 'doubtful', label: 'Needs review' },
  { key: 'invalid', label: 'Errors' },
  { key: 'unchanged', label: 'Unchanged' },
];

const ACCEPT = '.csv,.xlsx';

function Tile({ label, value, tone }: { label: string; value: number; tone?: string }) {
  return (
    <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-3">
      <p className="text-xs text-gray-500 dark:text-gray-400">{label}</p>
      <p className={`text-xl font-semibold ${tone ?? 'text-gray-900 dark:text-white'}`}>{value}</p>
    </div>
  );
}

function RowsTable({ rows, outcome }: { rows: ImportRow[]; outcome: ImportOutcome }) {
  if (rows.length === 0) return <p className="text-sm text-gray-500 dark:text-gray-400">No rows.</p>;
  return (
    <div className="overflow-x-auto">
      <table className="min-w-full text-sm">
        <thead>
          <tr className="text-left text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
            <th className="py-2 pr-3 font-medium">Row</th>
            <th className="py-2 pr-3 font-medium">Hospital</th>
            <th className="py-2 pr-3 font-medium">Pincode</th>
            <th className="py-2 font-medium">{outcome === 'invalid' ? 'Problem' : 'Detail'}</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
          {rows.map((row) => (
            <tr key={row.id} className="align-top">
              <td className="py-2 pr-3 text-gray-500">{row.row_number}</td>
              <td className="py-2 pr-3 text-gray-900 dark:text-white">{row.hospital_name}</td>
              <td className="py-2 pr-3 text-gray-700 dark:text-gray-300">{row.raw?.pincode}</td>
              <td className="py-2 text-gray-700 dark:text-gray-300">
                {outcome === 'invalid'
                  ? row.errors.map((e) => `${e.column}: ${e.message}`).join('; ')
                  : row.matched_hospital
                    ? `Linked to ${row.matched_hospital.name}`
                    : outcome === 'doubtful'
                      ? row.resolution === 'pending'
                        ? 'Waiting for the country admin'
                        : `${row.resolution.replace(/_/g, ' ')}${row.resolution_note ? ` — ${row.resolution_note}` : ''}`
                      : row.warnings.map((w) => w.message).join('; ')}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function BatchPreview({
  batch,
  canWrite,
  onChanged,
}: {
  batch: ImportBatchDetail;
  canWrite: boolean;
  onChanged: (batch: ImportBatchDetail | null) => void;
}) {
  const [tab, setTab] = useState<ImportOutcome>(batch.invalid_count > 0 ? 'invalid' : 'new');
  const [skipInvalid, setSkipInvalid] = useState(false);
  const queryClient = useQueryClient();
  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ['hospital-import-batches'] });
    queryClient.invalidateQueries({ queryKey: ['insurer-hospitals'] });
  };

  const commit = useMutation({
    mutationFn: () => hospitalNetworkApi.commit(batch.id),
    onSuccess: (data) => {
      refresh();
      onChanged(data);
      toast.success(
        data.doubtful_count > 0
          ? `List committed. ${data.doubtful_count} row(s) wait for the country admin.`
          : 'List committed.'
      );
    },
    onError: (err) => toast.error(getErrorMessage(err)),
  });
  const discard = useMutation({
    mutationFn: () => hospitalNetworkApi.discard(batch.id),
    onSuccess: () => {
      refresh();
      onChanged(null);
      toast.success('Preview discarded. Nothing was changed.');
    },
    onError: (err) => toast.error(getErrorMessage(err)),
  });

  const previewed = batch.status === 'previewed';
  // #645: the server refuses a complete list that has invalid rows (they would
  // switch hospitals off by mistake), so skipping is only offered otherwise.
  const fullListHasErrors = batch.is_full_list && batch.invalid_count > 0;
  const commitBlocked = fullListHasErrors || (batch.invalid_count > 0 && !skipInvalid);

  return (
    <Card>
      <CardHeader
        title={batch.original_name}
        subtitle={`${batch.insurer_name} · ${batch.status_display}${previewed ? ' — nothing has been changed yet' : ''}`}
        action={
          <Button
            size="sm"
            variant="outline"
            leftIcon={<ArrowDownTrayIcon className="w-4 h-4" />}
            onClick={() => hospitalNetworkApi.downloadErrors(batch.id).catch((err) => toast.error(getErrorMessage(err)))}
          >
            Error CSV
          </Button>
        }
      />
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        <Tile label="New" value={batch.new_count} />
        <Tile label="Matched" value={batch.matched_count} />
        <Tile label="Needs review" value={batch.doubtful_count} tone="text-amber-600" />
        <Tile label="Errors" value={batch.invalid_count} tone="text-red-600" />
        <Tile label="Unchanged" value={batch.unchanged_count} />
        <Tile label={batch.is_full_list ? 'Will remove' : 'Would remove'} value={batch.will_remove_count} />
      </div>
      {batch.missing_registration_count > 0 && (
        <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
          {batch.missing_registration_count} of {batch.row_count} rows have no registration number; those are matched by
          name and pincode only.
        </p>
      )}
      {(batch.held_back_count ?? 0) > 0 && previewed && (
        <p className="mt-2 text-xs text-gray-500 dark:text-gray-400" data-testid="held-back-note">
          {batch.held_back_count} hospital(s) in this file were removed by hand earlier and are counted as unchanged.
          This list will not switch them back on; use Restore on the Inactive tab if you want them back.
        </p>
      )}
      {batch.will_remove_count > 0 && previewed && (
        <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
          {batch.is_full_list
            ? `${batch.will_remove_count} hospital(s) on your current list are not in this file and will be switched off.`
            : `${batch.will_remove_count} hospital(s) on your current list are not in this file. They stay, because the file was not marked as the complete list.`}
        </p>
      )}

      <div className="mt-4 flex flex-wrap gap-2" role="tablist">
        {OUTCOME_TABS.map((item) => (
          <button
            key={item.key}
            type="button"
            role="tab"
            aria-selected={tab === item.key}
            onClick={() => setTab(item.key)}
            className={`rounded-full px-3 py-1 text-sm ${
              tab === item.key
                ? 'bg-primary-600 text-white'
                : 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-200'
            }`}
          >
            {item.label} ({batch.rows_by_outcome?.[item.key]?.length ?? 0})
          </button>
        ))}
      </div>
      <div className="mt-3">
        <RowsTable rows={batch.rows_by_outcome?.[tab] ?? []} outcome={tab} />
      </div>

      {canWrite && previewed && (
        <div className="mt-4 flex flex-col sm:flex-row sm:items-center gap-3">
          {fullListHasErrors && (
            <p role="alert" className="text-sm text-red-600 dark:text-red-400">
              This is marked as your complete list but {batch.invalid_count} row(s) have errors. Fix them and upload
              again, or upload without marking it complete. Nothing will be removed until then.
            </p>
          )}
          {batch.invalid_count > 0 && !fullListHasErrors && (
            <label className="inline-flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
              <input type="checkbox" checked={skipInvalid} onChange={(e) => setSkipInvalid(e.target.checked)} />
              Skip the {batch.invalid_count} row(s) with errors
            </label>
          )}
          <div className="flex gap-2 sm:ml-auto">
            <Button variant="outline" onClick={() => discard.mutate()} isLoading={discard.isPending}>
              Discard
            </Button>
            <Button onClick={() => commit.mutate()} isLoading={commit.isPending} disabled={commitBlocked}>
              Commit
            </Button>
          </div>
        </div>
      )}
    </Card>
  );
}

export function HospitalListUpload({
  insurerOptions,
  onPreview,
}: {
  /** Country admin only: choose the insurer the list belongs to. */
  insurerOptions?: { value: string; label: string }[];
  onPreview: (batch: ImportBatchDetail) => void;
}) {
  const fileRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [fileError, setFileError] = useState('');
  const [fullList, setFullList] = useState(false);
  const [insurer, setInsurer] = useState('');
  const queryClient = useQueryClient();

  const preview = useMutation({
    mutationFn: () => hospitalNetworkApi.preview({ file: file as File, isFullList: fullList, insurer: insurer || undefined }),
    onSuccess: (batch) => {
      queryClient.invalidateQueries({ queryKey: ['hospital-import-batches'] });
      onPreview(batch);
      setFile(null);
      if (fileRef.current) fileRef.current.value = '';
    },
    onError: (err) => toast.error(getErrorMessage(err)),
  });

  const needsInsurer = !!insurerOptions && !insurer;

  return (
    <Card>
      <CardHeader
        title="Upload hospital list"
        subtitle="CSV or Excel, up to 10 MB. You see a preview first; nothing changes until you commit."
        action={
          <Button
            size="sm"
            variant="outline"
            leftIcon={<ArrowDownTrayIcon className="w-4 h-4" />}
            onClick={() => hospitalNetworkApi.downloadTemplate().catch((err) => toast.error(getErrorMessage(err)))}
          >
            Download Template
          </Button>
        }
      />
      <div className="space-y-3">
        {insurerOptions && (
          <div>
            <label htmlFor="upload_insurer" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Insurer
            </label>
            <select
              id="upload_insurer"
              value={insurer}
              onChange={(e) => setInsurer(e.target.value)}
              className="w-full sm:w-80 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
            >
              <option value="">Choose the insurer</option>
              {insurerOptions.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>
        )}
        <div>
          <label htmlFor="hospital_list_file" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            File
          </label>
          <input
            id="hospital_list_file"
            ref={fileRef}
            type="file"
            accept={ACCEPT}
            onChange={(e) => {
              const chosen = e.target.files?.[0] ?? null;
              const problem = chosen ? validateHospitalListFile(chosen) : null;
              setFileError(problem ?? '');
              setFile(problem ? null : chosen);
            }}
            className="block w-full text-sm text-gray-700 dark:text-gray-300"
          />
          {fileError && <p className="mt-1 text-sm text-red-600 dark:text-red-400">{fileError}</p>}
        </div>
        <label className="inline-flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
          <input type="checkbox" checked={fullList} onChange={(e) => setFullList(e.target.checked)} />
          This file is the complete list (hospitals missing from it are switched off on commit)
        </label>
        <div>
          <Button
            onClick={() => preview.mutate()}
            isLoading={preview.isPending}
            disabled={!file || needsInsurer}
            leftIcon={<DocumentArrowUpIcon className="w-4 h-4" />}
          >
            Preview
          </Button>
        </div>
      </div>
    </Card>
  );
}

export function BatchStatusBadge({ status, label }: { status: string; label: string }) {
  const variant = status === 'committed' ? 'success' : status === 'previewed' ? 'warning' : 'default';
  return (
    <Badge size="sm" variant={variant}>
      {label}
    </Badge>
  );
}
